import { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, ClipboardCheck, Database, FileText, Layers, LayoutGrid, LogOut, Menu, Package, PackageSearch, ShoppingBag, Truck, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { backTarget, isDeptActive, visibleDepartments } from "@/lib/departments";
import api from "@/lib/api";

const READY_COUNT_ROLES = ["owner", "receiving"];

function useReadyWaitingCount(role, pathname) {
  const [count, setCount] = useState(0);
  const enabled = READY_COUNT_ROLES.includes(role);
  const load = useCallback(() => {
    if (!enabled) return;
    api.get("/ready-items/count").then((r) => setCount(r.data.waiting_to_keep || 0)).catch(() => {});
  }, [enabled]);
  useEffect(() => {
    load();
  }, [load, pathname]);
  useEffect(() => {
    window.addEventListener("ht:ready-changed", load);
    return () => window.removeEventListener("ht:ready-changed", load);
  }, [load]);
  return count;
}

const CountDot = ({ n, testid }) =>
  n > 0 ? (
    <span data-testid={testid} className="absolute -right-2 -top-1.5 min-w-[18px] rounded-full bg-[#B66F7A] px-1 text-center text-[10px] font-bold leading-[18px] text-white">
      {n}
    </span>
  ) : null;

// Original staff navigation (pre-department consolidation) — unchanged role gating.
const STAFF_TABS = [
  { to: "/", label: "Batches", icon: LayoutGrid, end: false, roles: null, key: "batches" },
  { to: "/orders", label: "Orders", icon: ShoppingBag, roles: ["admin_input", "viewer"], key: "orders" },
  { to: "/allocations", label: "FIFO", icon: Layers, roles: ["admin", "admin_input", "viewer"], key: "allocations" },
  { to: "/receiving", label: "Receiving", icon: PackageSearch, end: true, roles: ["receiving", "admin", "purchaser", "viewer"], key: "receiving" },
  { to: "/receiving/approvals", label: "Approvals", icon: ClipboardCheck, roles: ["admin", "admin_input"], key: "approvals" },
  { to: "/team", label: "Team", icon: Users, roles: [], key: "team" },
];

const STAFF_MORE = [
  { to: "/packing", label: "Packing", icon: Package, roles: null },
  { to: "/shipping", label: "Shipping", icon: Truck, roles: ["admin_input", "viewer"] },
  { to: "/invoices", label: "Invoices", icon: FileText, roles: ["admin_input", "admin"] },
  { to: "/masters", label: "Product Master", icon: Database, roles: [] },
];

const allowed = (item, role) => !item.roles || item.roles.includes(role);

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);
  const isOwner = user.role === "owner";
  const departments = visibleDepartments(user.role);
  const staffTabs = STAFF_TABS.filter((t) => allowed(t, user.role));
  const staffMore = STAFF_MORE.filter((t) => allowed(t, user.role));
  const back = backTarget(location.pathname, user.role);
  const readyCount = useReadyWaitingCount(user.role, location.pathname);
  const badgeFor = (key) => (key === "operations" || key === "receiving" ? readyCount : 0);

  const linkClass = (active) =>
    `rounded-md px-3 py-2 text-sm font-medium transition-colors ${
      active ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:bg-accent/60"
    }`;
  const tabClass = (active) =>
    `flex h-16 flex-1 flex-col items-center justify-center gap-1 px-1 text-[11px] font-semibold transition-colors ${
      active ? "text-primary" : "text-muted-foreground"
    }`;

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b border-border/70 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex h-14 max-w-5xl items-center gap-2.5 px-4">
          <button onClick={() => navigate("/")} className="shrink-0" aria-label="Home" data-testid="header-logo">
            <img src="/brand/heels-and-tea-logo.jpg" alt="Heels & Tea" className="h-9 w-auto mix-blend-multiply" />
          </button>
          <span data-testid="header-brand" className="font-heading text-base font-extrabold tracking-tight">
            H&amp;T Operations
          </span>
          <span
            data-testid="user-role-badge"
            className="hidden rounded-md border border-rose-100 bg-rose-50 px-2 py-0.5 text-xs font-semibold text-rose-700 sm:inline"
          >
            {ROLE_LABELS[user.role] || user.role}
          </span>
          <nav className="ml-auto hidden items-center gap-1 md:flex">
            {isOwner
              ? departments.map((d) => (
                  <NavLink key={d.key} to={d.to} data-testid={`nav-${d.key}-desktop`} className={linkClass(isDeptActive(d, location.pathname))}>
                    {d.label}
                  </NavLink>
                ))
              : [...staffTabs, ...staffMore].map((t) => (
                  <NavLink key={t.to} to={t.to} end={t.end} data-testid={`nav-staff-${t.label.toLowerCase().replace(/\s+/g, "-")}-desktop`} className={({ isActive }) => linkClass(isActive)}>
                    {t.label}
                  </NavLink>
                ))}
          </nav>
          <span className="ml-auto hidden max-w-[120px] truncate text-sm text-muted-foreground sm:block md:ml-2">{user.name}</span>
          <Button variant="ghost" size="icon" data-testid="logout-button" onClick={logout} className="h-10 w-10" aria-label="Log out">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 pb-28 pt-4 md:pb-12">
        {back && (
          <button
            data-testid="global-back-button"
            onClick={() => navigate(back.to)}
            className="mb-3 flex h-10 items-center gap-1 text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" /> {back.label}
          </button>
        )}
        <Outlet />
      </main>

      <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-border/70 bg-white/95 backdrop-blur-xl md:hidden">
        <div className="mx-auto flex max-w-5xl">
          {isOwner
            ? departments.map((d) => {
                const Icon = d.icon;
                return (
                  <NavLink key={d.key} to={d.to} data-testid={`nav-${d.key}-mobile`} className={tabClass(isDeptActive(d, location.pathname))}>
                    <span className="relative">
                      <Icon className="h-5 w-5" />
                      <CountDot n={badgeFor(d.key)} testid={`nav-${d.key}-ready-count`} />
                    </span>
                    <span className="truncate">{d.label}</span>
                  </NavLink>
                );
              })
            : (
              <>
                {staffTabs.map((t) => {
                  const Icon = t.icon;
                  return (
                    <NavLink
                      key={t.to}
                      to={t.to}
                      end={t.end}
                      data-testid={`nav-staff-${t.key}-mobile`}
                      className={({ isActive }) => tabClass(isActive)}
                    >
                      <span className="relative">
                        <Icon className="h-5 w-5" />
                        <CountDot n={badgeFor(t.key)} testid={`nav-staff-${t.key}-ready-count`} />
                      </span>
                      <span className="truncate">{t.label}</span>
                    </NavLink>
                  );
                })}
                {staffMore.length > 0 && (
                  <button data-testid="nav-more-mobile" onClick={() => setMoreOpen(true)} className={tabClass(false)}>
                    <Menu className="h-5 w-5" />
                    <span>More</span>
                  </button>
                )}
              </>
            )}
        </div>
      </nav>

      <Drawer open={moreOpen} onOpenChange={setMoreOpen} shouldScaleBackground={false}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle className="font-heading">More</DrawerTitle>
          </DrawerHeader>
          <div className="space-y-1 px-4 pb-8" data-testid="more-menu">
            {staffMore.map((t) => {
              const Icon = t.icon;
              return (
                <NavLink
                  key={t.to}
                  to={t.to}
                  data-testid={`nav-staff-${t.label.toLowerCase().replace(/\s+/g, "-")}-mobile`}
                  onClick={() => setMoreOpen(false)}
                  className="flex h-12 items-center gap-3 rounded-md px-3 text-sm font-semibold transition-colors hover:bg-accent"
                >
                  <Icon className="h-4 w-4" /> {t.label}
                </NavLink>
              );
            })}
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
